import { createContext, provide } from '@lit/context'
import { SchmancyElement } from '@schmancy/mixins/index'
import { SurfaceMixin } from '@schmancy/mixins/surface.mixin'
import { TSurfaceColor } from '@schmancy/types'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'

export const SchmancySurfaceTypeContext = createContext<TSurfaceColor>('surface')

/**
 * Paints a translucent liquid-glass background (tonal `type` + `opacity`
 * attribute) and sets the inherited text color for descendants. A painted
 * surface takes the ladder's `2xl` corner unless the consumer names one;
 * shadow, cursor, and sizing stay the consumer's responsibility via Tailwind
 * classes on the element.
 */
@customElement('schmancy-surface')
export class SchmancySurface extends SurfaceMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
				box-sizing: border-box;
				overflow: visible;
			}

			/* The corner a painted surface takes when the consumer names none.
			   Scoped by the same pane-vs-ground line the glass hairline is scoped
			   on, for the same reason: a pane has corners, and a page ground does
			   not. Measured live across the storefront and the studio, every
			   unclassed surface/surfaceDim/surfaceBright/containerLowest is
			   viewport-sized — the storefront root, the sign-in ground, the
			   event-page column — where a corner cuts the page open at the
			   browser edge and shows the body through the notch. transparent is
			   out for a different reason: it paints nothing, so it has no corner
			   to round. Everything else painted is in, including a semantic tint
			   and a type added later, which is the right default for a type whose
			   whole job is to paint a pane.

			   :not([class*='rounded']) is what makes "unless the consumer names
			   one" true rather than nearly true. A full rounded-* compiles to the
			   border-radius SHORTHAND and outranks this from the outer tree
			   outright, so those consumers need no guard. A sided rounded-b-2xl
			   compiles to two longhands alone, and without this guard the
			   remaining two corners would keep resolving here — silently
			   reshaping the 15 live consumers that name some corners and leave
			   the rest square, every asymmetric identity header among them.

			   It lives on this component rather than in the shared surfaceStyles
			   because <schmancy-details> and <schmancy-expand-root> mix those in
			   too and each already owns its corner — details on the xl rung
			   matched to its own inner layers, expand-root on its portal panel. */
			:host(
				[type]:not([type='surface']):not([type='surfaceDim']):not([type='surfaceBright']):not(
						[type='containerLowest']
					):not([type='transparent']):not([class*='rounded'])
			) {
				border-radius: var(--schmancy-radius-2xl, 1.25rem);
			}
		`,
	]
	/** Provided to descendant components via context. */
	@provide({ context: SchmancySurfaceTypeContext })
	@property({ reflect: true })
	override type: TSurfaceColor = 'containerLow'

	/**
	 * Turns on backdrop blur — the separation cue for floating/grouping
	 * chrome (a sticky footer, a card over content). Orthogonal to `opacity`:
	 * the tonal fill scale stays flat by default (no `backdrop-filter`) so a
	 * tiled scroll list never stacks blur layers; `elevated` is the only flag
	 * that turns it on, for the one surface that's actually floating.
	 */
	@property({ type: Boolean, reflect: true })
	elevated = false

	protected render(): unknown {
		return html`<slot></slot>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-surface': SchmancySurface
	}
}
